import { useAtom, useAtomValue } from "jotai";
import CurrentPublicationEntityAtom from "utilities/atoms/current-publication-entity-atom";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import SearchSortOrderBy from "models/enumerations/sorting/search-results/search-sort-order-by";
import { BookviewSearchTermAtom } from "utilities/atoms/bookview/bookview-search-term-atom";
import { useEffect } from "react";
import { BookviewSearchResultsAtom } from "utilities/atoms/bookview/bookview-search-results-atom";
import { PublicationEntityTypeConstants } from "constants/publication-entity-type-constants";
import usePagedResults, {
    UsePagedResultsData,
} from "utilities/hooks/use-paged-results";
import HitRecord from "models/view-models/search/hit-record";
import { useBookviewSearchQuery } from "utilities/hooks/aspect/search/use-bookview-search-query";
import { BookviewSearchScopeAtom } from "utilities/atoms/bookview/bookview-search-scope-atom";
import { BookviewSearchScope } from "organisms/panels/bookview-tab-panels/bookview-search/bookview-search-scope";
import { useGlobalState } from "../use-global-state-context";

const DEFAULT_TAKE = 100;

interface BookviewSearchPagedResults
    extends Pick<
        UsePagedResultsData,
        | "currentPage"
        | "numberOfPages"
        | "onPageLast"
        | "onPageNext"
        | "onSetPage"
        | "resetPagination"
        | "take"
    > {}

export interface UseBookviewSearchReturn extends BookviewSearchPagedResults {
    searchText: string;
    handleTextChange: (searchText: string) => void;
    search: () => void;
    loading: boolean;
    searchResults: HitRecord[];
    reset: () => void;
    clearSearch: () => void;
    totalResults: number;
}

const useBookviewSearch = (isDisabled: boolean): UseBookviewSearchReturn => {
    const publicationEntity = useAtomValue(CurrentPublicationEntityAtom);
    const { publication } = useCurrentPublication();
    const [searchText, setSearchText] = useAtom(BookviewSearchTermAtom);
    const [searchResults, setSearchResults] = useAtom(
        BookviewSearchResultsAtom
    );
    const searchScope = useAtomValue(BookviewSearchScopeAtom);

    const showEnhancedContentSetting =
        useGlobalState().globalState.getUserSettingValue("EnhancedContentShow");

    const {
        currentPage,
        numberOfPages,
        onPageLast,
        onPageNext,
        onSetPage,
        resetPagination,
        setRowCount,
        skip,
        take,
    } = usePagedResults(DEFAULT_TAKE);

    const addEnhancedContent = showEnhancedContentSetting
        ? PublicationEntityTypeConstants.EnhancedContent
        : "";

    const {
        searchResults: resultObject,
        rowCount,
        reset,
        refetchResults: search,
        isLoading: loading,
    } = useBookviewSearchQuery(
        {
            ...(searchScope === BookviewSearchScope.CurrentView &&
                publicationEntity?.getEntityIdParams()),
            entityTypes: [
                PublicationEntityTypeConstants.Chapter,
                PublicationEntityTypeConstants.Annex,
                PublicationEntityTypeConstants.Article,
                PublicationEntityTypeConstants.Part,
                PublicationEntityTypeConstants.Section,
                addEnhancedContent,
            ],
            publicationIds: [publication?.id!],
            sortOrder: SearchSortOrderBy.PrintOrder,
            search: searchText,
            take,
            start: skip,
        },
        isDisabled
    );

    const handleTextChange = (newSearchText: string) => {
        setSearchText(newSearchText);
    };

    const clearSearch = () => {
        setSearchText("");
        reset();
        resetPagination();
    };

    useEffect(() => {
        setSearchResults(resultObject ?? []);
    }, [resultObject, setSearchResults]);

    useEffect(() => {
        setRowCount(rowCount ?? 0);
    }, [rowCount, setRowCount]);

    useEffect(() => {
        if (isDisabled) {
            clearSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDisabled, publication]);

    // Reset pagination on publication entity change (i.e., moving to new chapter, article, publication, etc.) or scope change
    useEffect(() => {
        resetPagination();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publicationEntity?.entityId, searchScope, showEnhancedContentSetting]);

    return {
        searchText,
        handleTextChange,
        search,
        loading,
        searchResults,
        reset,
        clearSearch,
        totalResults: rowCount ?? 0,
        currentPage,
        numberOfPages,
        onPageLast,
        onPageNext,
        onSetPage,
        resetPagination,
        take,
    };
};
export default useBookviewSearch;
